/**
 * EarningsDashboardPage — `/dashboard/earnings`. Auth required.
 *
 * Mock data today; the real settlement API arrives with the Phase 4.9
 * `gi-royalty` sidecar. The shape is what real wiring will land into:
 *
 *   - 12-month revenue series → recharts AreaChart
 *   - per-product revenue table
 *   - recent sales table with the 88 / 10 / 2 royalty split rendered
 *     as horizontal bars
 *   - fork-royalty summary (separate from primary sales)
 *
 * Lives inside `RegularUserShell` so the sidebar / nav are intact —
 * the brief says "auth required (creator role)". Today every signed-in
 * user lands here; once the creator-role gate ships this page wraps
 * in `<CreatorGuard>` (mirroring the existing `<AdminGuard>`).
 */

import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber } from '@/utils/format';
import { getOfficialListings } from '@/data/defaultProducts';

const ACCENT = '#6366f1';
const ACCENT_SOFT = '#eef2ff';
const ROYALTY_AUTHOR = 88;
const ROYALTY_ENGINE = 10;
const ROYALTY_FOUNDATION = 2;

interface MonthlyRevenue {
  month: string; // ISO YYYY-MM
  label: string; // 'Jan', 'Feb', …
  primary_usd: number;
  fork_usd: number;
}

interface SaleRow {
  id: string;
  product_id: string;
  product_name: string;
  date: string; // ISO
  amount_usd: number;
  is_fork: boolean;
}

function buildMonthlySeries(): MonthlyRevenue[] {
  // Last 12 months ending this month. Synthetic curve that grows
  // and dips so the chart looks alive without lying about what's
  // really happening. Replaced by the settlement API at wire-time.
  const now = new Date();
  const out: MonthlyRevenue[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const seed = (d.getMonth() + 1) * 113;
    const primary = 1_400 + (seed % 1_300);
    const fork = 180 + (seed % 240);
    out.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString(undefined, { month: 'short' }),
      primary_usd: primary,
      fork_usd: fork,
    });
  }
  return out;
}

function buildRecentSales(): SaleRow[] {
  // Twelve most recent sales — synthetic but deterministic against
  // the Official catalogue so reloading doesn't shuffle.
  const sales: SaleRow[] = [];
  const products = getOfficialListings('en');
  for (let i = 0; i < 12; i++) {
    const product = products[i % products.length]!;
    const base = product.price_usd;
    const days_ago = i * 3 + (i % 4);
    const date = new Date();
    date.setDate(date.getDate() - days_ago);
    const isFork = i % 5 === 0;
    sales.push({
      id: `sale_${i}`,
      product_id: product.root_id,
      product_name: product.name,
      date: date.toISOString(),
      amount_usd: isFork ? Math.round(base * 0.1) : base,
      is_fork: isFork,
    });
  }
  return sales;
}

export function EarningsDashboardPage() {
  const locale = useLocaleStore((s) => s.locale);
  const series = useMemo(buildMonthlySeries, []);
  const sales = useMemo(buildRecentSales, []);
  const products = useMemo(() => getOfficialListings(locale), [locale]);

  const totals = useMemo(() => {
    const total_primary = series.reduce((acc, m) => acc + m.primary_usd, 0);
    const total_fork = series.reduce((acc, m) => acc + m.fork_usd, 0);
    const this_month = series[series.length - 1]!;
    const last_month = series[series.length - 2];
    const month_delta_pct = last_month
      ? Math.round(
          ((this_month.primary_usd - last_month.primary_usd) /
            Math.max(last_month.primary_usd, 1)) *
            100,
        )
      : 0;
    return {
      total_primary,
      total_fork,
      this_month_primary: this_month.primary_usd,
      this_month_fork: this_month.fork_usd,
      month_delta_pct,
    };
  }, [series]);

  const perProductRevenue = useMemo(() => {
    // Spread the synthetic year's primary revenue across the 7
    // products with a deterministic share so each product has a
    // believable line in the table.
    const total = totals.total_primary;
    return products.map((p, i) => {
      const share = [0.22, 0.18, 0.16, 0.13, 0.12, 0.10, 0.09][i] ?? 0.05;
      return {
        product_id: p.root_id,
        name: p.name,
        revenue_usd: Math.round(total * share),
        units: Math.max(1, Math.round((total * share) / Math.max(p.price_usd, 1))),
      };
    });
  }, [products, totals.total_primary]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Earnings</h1>
        <p className="text-sm text-ink-muted">
          Revenue, royalties, and fork chains across your catalogue. Numbers
          below are mock previews — the live settlement wire is on the Phase
          4.9 roadmap.
        </p>
      </header>

      {/* KPI strip */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          label="Total revenue (12 mo)"
          value={`$${formatNumber(totals.total_primary, locale)}`}
        />
        <Kpi
          label="This month"
          value={`$${formatNumber(totals.this_month_primary, locale)}`}
          delta={totals.month_delta_pct}
        />
        <Kpi
          label="Fork royalties (12 mo)"
          value={`$${formatNumber(totals.total_fork, locale)}`}
        />
        <Kpi
          label="Royalty split"
          value={`${ROYALTY_AUTHOR}/${ROYALTY_ENGINE}/${ROYALTY_FOUNDATION}`}
          subtitle="Author · Engine · Foundation"
        />
      </section>

      {/* Chart */}
      <Card>
        <CardTitle>Revenue over time</CardTitle>
        <CardSubtitle>Primary sales + fork royalties, last 12 months.</CardSubtitle>
        <div className="mt-4 h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-primary" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="grad-fork" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#71717a' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#71717a' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `$${Math.round(v / 100) / 10}k`}
                width={50}
              />
              <Tooltip
                cursor={{ stroke: ACCENT, strokeOpacity: 0.4 }}
                contentStyle={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v, k) => [
                  `$${formatNumber(typeof v === 'number' ? v : Number(v) || 0, locale)}`,
                  k === 'primary_usd' ? 'Primary' : 'Fork royalties',
                ]}
              />
              <Area
                type="monotone"
                dataKey="primary_usd"
                stroke={ACCENT}
                strokeWidth={2}
                fill="url(#grad-primary)"
              />
              <Area
                type="monotone"
                dataKey="fork_usd"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#grad-fork)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Per-product breakdown */}
      <Card>
        <CardTitle>Per-product revenue</CardTitle>
        <CardSubtitle>12-month rolling totals.</CardSubtitle>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-start text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
                <th className="py-2">Product</th>
                <th className="py-2 text-end">Units</th>
                <th className="py-2 text-end">Revenue</th>
                <th className="py-2">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {perProductRevenue.map((row) => {
                const pct = totals.total_primary
                  ? Math.round((row.revenue_usd / totals.total_primary) * 100)
                  : 0;
                return (
                  <tr key={row.product_id}>
                    <td className="py-2 pe-3 font-medium text-ink">{row.name}</td>
                    <td className="py-2 text-end tabular-nums">{row.units}</td>
                    <td className="py-2 text-end tabular-nums">
                      ${formatNumber(row.revenue_usd, locale)}
                    </td>
                    <td className="py-2 ps-3" style={{ minWidth: 120 }}>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: ACCENT,
                            }}
                          />
                        </div>
                        <span className="w-9 text-end text-[11px] text-ink-subtle">
                          {pct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Royalty split diagram */}
      <Card>
        <CardTitle>Royalty split</CardTitle>
        <CardSubtitle>
          Per-sale split enforced at settlement (gi-royalty). 88 / 10 / 2.
        </CardSubtitle>
        <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full">
          <div style={{ width: `${ROYALTY_AUTHOR}%`, backgroundColor: ACCENT }} aria-hidden />
          <div style={{ width: `${ROYALTY_ENGINE}%`, backgroundColor: '#a78bfa' }} aria-hidden />
          <div style={{ width: `${ROYALTY_FOUNDATION}%`, backgroundColor: '#fbbf24' }} aria-hidden />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <SplitLegend color={ACCENT} label="Author" pct={ROYALTY_AUTHOR} />
          <SplitLegend color="#a78bfa" label="Engine" pct={ROYALTY_ENGINE} />
          <SplitLegend color="#fbbf24" label="Foundation" pct={ROYALTY_FOUNDATION} />
        </div>
      </Card>

      {/* Recent sales table */}
      <Card>
        <CardTitle>Recent sales</CardTitle>
        <CardSubtitle>Last twelve sales across primary and fork events.</CardSubtitle>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-start text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
                <th className="py-2">Date</th>
                <th className="py-2">Product</th>
                <th className="py-2">Type</th>
                <th className="py-2 text-end">Amount</th>
                <th className="py-2 text-end">Author</th>
                <th className="py-2 text-end">Engine</th>
                <th className="py-2 text-end">Foundation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sales.map((s) => {
                const author = (s.amount_usd * ROYALTY_AUTHOR) / 100;
                const engine = (s.amount_usd * ROYALTY_ENGINE) / 100;
                const foundation = (s.amount_usd * ROYALTY_FOUNDATION) / 100;
                return (
                  <tr key={s.id}>
                    <td className="py-2 pe-3 text-xs text-ink-muted tabular-nums">
                      {new Date(s.date).toLocaleDateString(locale, {
                        month: 'short',
                        day: '2-digit',
                      })}
                    </td>
                    <td className="py-2 pe-3 text-ink">{s.product_name}</td>
                    <td className="py-2 pe-3">
                      {s.is_fork ? (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
                        >
                          Fork
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ backgroundColor: ACCENT_SOFT, color: ACCENT }}
                        >
                          Primary
                        </span>
                      )}
                    </td>
                    <td className="py-2 pe-3 text-end tabular-nums font-semibold text-ink">
                      ${formatNumber(s.amount_usd, locale)}
                    </td>
                    <td className="py-2 pe-3 text-end tabular-nums text-ink-muted">
                      ${formatNumber(Math.round(author), locale)}
                    </td>
                    <td className="py-2 pe-3 text-end tabular-nums text-ink-muted">
                      ${formatNumber(Math.round(engine), locale)}
                    </td>
                    <td className="py-2 text-end tabular-nums text-ink-muted">
                      ${formatNumber(Math.round(foundation), locale)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-[11px] text-ink-subtle">
          {sales.filter((s) => s.is_fork).length} of {sales.length} sales were
          fork royalties (someone built on top of one of your products).
        </p>
      </Card>
    </div>
  );
}

function Kpi({
  label,
  value,
  delta,
  subtitle,
}: {
  label: string;
  value: string;
  delta?: number;
  subtitle?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-semibold text-ink">{value}</div>
        {typeof delta === 'number' ? (
          <span
            className="text-xs font-semibold"
            style={{ color: delta >= 0 ? '#15803d' : '#b91c1c' }}
          >
            {delta >= 0 ? '+' : ''}
            {delta}%
          </span>
        ) : null}
      </div>
      {subtitle ? (
        <div className="mt-1 text-[11px] text-ink-subtle">{subtitle}</div>
      ) : null}
    </div>
  );
}

function SplitLegend({
  color,
  label,
  pct,
}: {
  color: string;
  label: string;
  pct: number;
}) {
  return (
    <div className="inline-flex items-center gap-2">
      <span
        className="h-3 w-3 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="text-ink-muted">
        {label} <span className="font-semibold text-ink">{pct}%</span>
      </span>
    </div>
  );
}
